import { useState } from "react";
import { LayoutGrid, Rows3 } from "lucide-react";

const isSourced = (url, citation) => !!url || (!!citation && !/ideavalidator360|estimate|survey of|founder/i.test(citation));
const TILES = [
  { n: "01", title: "Core Diagnosis & Reality Check", badge: () => "Analyst Verdict", tone: "blue", body: (s) => s.tile1_diagnosis },
  { n: "02", title: "Verified Market Evidence", badge: (s) => (isSourced(s.tile2_source_url, s.tile2_fact_citation) ? "Sourced Fact" : "Method Estimate"),
    tone: "emerald", body: (s) => s.tile2_fact_text,
    bar: (s) => `Source: ${s.tile2_fact_citation}${s.tile2_fact_date ? ` · ${s.tile2_fact_date}` : ""}`, barLabel: "Source" },
  { n: "03", title: "Unit Math & Key Unknown", badge: () => "Method Estimate", tone: "amber", body: (s) => s.tile3_unit_math, bar: (s) => s.tile3_key_unknown, barLabel: "Key Unknown to Validate" },
  { n: "04", title: "Immediate 7-Day Execution Step", badge: () => "Do This Next", tone: "cyan", body: (s) => s.tile4_action_step, bar: (s) => s.tile4_target_metric, barLabel: "Target Metric" },
];
const TONE = {
  blue: "border-blue-400/30 text-blue-300 bg-blue-500/10",
  emerald: "border-emerald-400/30 text-emerald-300 bg-emerald-500/10",
  amber: "border-amber-400/30 text-amber-300 bg-amber-500/10",
  cyan: "border-cyan-400/30 text-cyan-300 bg-cyan-500/10",
};

const Tile = ({ t, s, testId }) => (
  <div className="flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-white/[0.07] bg-white/[0.02]" data-testid={testId}>
    <div className={`flex items-center justify-between gap-2 border-b px-3 py-2 ${TONE[t.tone]}`}>
      <p className="text-[11px] font-bold">{t.n}. {t.title}</p>
      <span className="shrink-0 rounded-full border border-current px-1.5 py-0.5 text-[9px] font-bold" data-testid={`${testId}-badge`}>{t.badge(s)}</span>
    </div>
    <p className={`flex-1 px-3 py-2.5 text-xs leading-relaxed text-slate-200 ${t.tone === "amber" ? "font-mono" : ""}`}>{t.body(s)}</p>
    {t.bar && <p className="border-t border-white/[0.06] bg-white/[0.02] px-3 py-2 text-[10px] text-slate-400"><span className="font-bold text-slate-300">{t.barLabel}: </span>{t.bar(s).replace(/^Source: /, "")}</p>}
  </div>
);

export const TeardownSection = ({ sections }) => {
  const [view, setView] = useState("grid");
  const btn = (v, Icon, label) => (
    <button type="button" onClick={() => setView(v)} aria-pressed={view === v} data-testid={`teardown-view-${v}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${view === v ? "bg-[#2563EB] text-white" : "text-slate-300 hover:text-white"}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" /> {label}
    </button>
  );
  return (
    <section aria-labelledby="teardown-title" data-testid="teardown-section" data-view={view}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 id="teardown-title" className="text-lg font-bold text-white">7-Section Investor & Technical Teardown</h2>
        <div className="inline-flex rounded-full border border-white/10 bg-white/[0.03] p-1">
          {btn("grid", LayoutGrid, "Uniform 4-Tile Bento Grid")}
          {btn("rows", Rows3, "Uniform Horizontal Audit Rows")}
        </div>
      </div>
      <div className="space-y-4">
        {sections.map((s) => (
          <article key={s.section_order} className="iv-glass rounded-2xl p-4" data-testid={`teardown-section-${s.section_order}`}>
            <h3 className="mb-3 text-sm font-bold text-white"><span className="font-mono text-cyan-300">{s.section_order}.</span> {s.section_title}</h3>
            <div className={view === "grid" ? "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" : "grid grid-cols-1 gap-2"}>
              {TILES.map((t) => <Tile key={t.n} t={t} s={s} testId={`teardown-${s.section_order}-tile-${t.n}`} />)}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};
